import { BookOpen, Key, Gauge, AlertTriangle } from 'lucide-react';
import { EndpointCard } from '@/components/academy/EndpointCard';
import { SectionNav } from '@/components/academy/SectionNav';
import { CodeBlock } from '@/components/academy/CodeBlock';
import { CopyPageButton } from '@/components/academy/CopyPageButton';

const NAV_SECTIONS = [
  { id: 'introduction', label: 'Introduction' },
  {
    id: 'ai-operations',
    label: 'AI Operations',
    items: [
      { id: 'research-company', label: 'Research Company', method: 'POST' as const },
      { id: 'generate-leads', label: 'Generate Leads', method: 'POST' as const },
    ],
  },
  {
    id: 'email-operations',
    label: 'Email Operations',
    items: [
      { id: 'generate-email', label: 'Generate Email', method: 'POST' as const },
      { id: 'generate-email-batch', label: 'Generate Batch', method: 'POST' as const },
      { id: 'get-email', label: 'Get Email', method: 'GET' as const },
      { id: 'build-draft-payload', label: 'Build Draft', method: 'POST' as const },
    ],
  },
  {
    id: 'leads',
    label: 'Leads',
    items: [
      { id: 'list-leads', label: 'List Leads', method: 'GET' as const },
      { id: 'create-lead', label: 'Create Lead', method: 'POST' as const },
      { id: 'get-lead', label: 'Get Lead', method: 'GET' as const },
      { id: 'update-lead', label: 'Update Lead', method: 'PATCH' as const },
      { id: 'delete-lead', label: 'Delete Lead', method: 'DELETE' as const },
    ],
  },
  {
    id: 'lead-groups',
    label: 'Lead Groups',
    items: [
      { id: 'list-lead-groups', label: 'List Groups', method: 'GET' as const },
      { id: 'create-lead-group', label: 'Create Group', method: 'POST' as const },
      { id: 'get-lead-group-leads', label: 'Group Leads', method: 'GET' as const },
    ],
  },
  {
    id: 'usage',
    label: 'Usage',
    items: [{ id: 'get-usage', label: 'Get Usage', method: 'GET' as const }],
  },
];

export function AcademyApiReferencePage() {
  return (
    <div className="animate-fade-in flex gap-8">
      {/* Sticky nav - hidden on mobile */}
      <aside className="hidden lg:block w-56 shrink-0">
        <SectionNav sections={NAV_SECTIONS} />
      </aside>

      {/* Main content */}
      <div id="api-reference-content" className="flex-1 min-w-0 space-y-12 max-w-4xl">
        {/* ------------------------------------------------------------------ */}
        {/* Introduction                                                        */}
        {/* ------------------------------------------------------------------ */}
        <section id="introduction" className="glass-card rounded-xl p-6 space-y-8">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">API Reference</h2>
            </div>
            <CopyPageButton contentSelector="#api-reference-content" title="HyPeMa API Reference" />
          </div>
          <p className="text-base-content/70">
            Complete reference for the HyPeMa REST API. All endpoints are versioned under{' '}
            <code className="text-sm bg-base-300/50 px-1.5 py-0.5 rounded">/v1</code> and return
            JSON responses.
          </p>

          {/* Base URL */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Base URL</h3>
            <CodeBlock language="text" code="https://api.hypema.app/api/v1" />
          </div>

          {/* Authentication */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-secondary" />
              <h3 className="text-lg font-semibold">Authentication</h3>
            </div>
            <p className="text-base-content/70">
              All requests require an API key in the Authorization header:
            </p>
            <CodeBlock language="text" code="Authorization: Bearer hpm_live_your_key_here" />
            <p className="text-base-content/70 text-sm">
              Create API keys in <strong>Settings &rarr; API Keys</strong>.
            </p>
          </div>

          {/* Response Envelope */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Response Envelope</h3>
            <p className="text-base-content/70">Every response follows this structure:</p>
            <CodeBlock
              language="json"
              code={`{
  "data": { ... },
  "error": null,
  "meta": {
    "requestId": "req_abc123",
    "tokensConsumed": 30
  }
}`}
            />
            <p className="text-base-content/50 text-xs">
              AI-powered endpoints return the actual HyPeMa tokens consumed
              in <code className="bg-base-300/50 px-1 py-0.5 rounded">tokensConsumed</code>.
              Non-AI endpoints return 0.
              The <code className="bg-base-300/50 px-1 py-0.5 rounded">GET /usage</code> endpoint
              additionally returns <code className="bg-base-300/50 px-1 py-0.5 rounded">tokensRemaining</code>.
            </p>
          </div>

          {/* Rate Limiting */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Gauge className="w-5 h-5 text-accent" />
              <h3 className="text-lg font-semibold">Rate Limiting</h3>
            </div>
            <p className="text-base-content/70">
              Complexity-based rate limiting. Each endpoint has a cost weight:
            </p>
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Weight</th>
                    <th>Examples</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Read</td>
                    <td>1</td>
                    <td className="text-base-content/60">GET /leads, GET /usage</td>
                  </tr>
                  <tr>
                    <td>Write</td>
                    <td>2</td>
                    <td className="text-base-content/60">POST /leads, PATCH /leads/:id</td>
                  </tr>
                  <tr>
                    <td>AI (single)</td>
                    <td>5</td>
                    <td className="text-base-content/60">POST /emails/generate</td>
                  </tr>
                  <tr>
                    <td>AI (heavy)</td>
                    <td>10-15</td>
                    <td className="text-base-content/60">
                      POST /ai/research-company, POST /emails/generate-batch
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-base-content/70 text-sm">
              Headers:{' '}
              <code className="text-xs bg-base-300/50 px-1 py-0.5 rounded">X-RateLimit-Limit</code>,{' '}
              <code className="text-xs bg-base-300/50 px-1 py-0.5 rounded">X-RateLimit-Remaining</code>,{' '}
              <code className="text-xs bg-base-300/50 px-1 py-0.5 rounded">X-RateLimit-Reset</code>,{' '}
              <code className="text-xs bg-base-300/50 px-1 py-0.5 rounded">X-RateLimit-Cost</code>
            </p>
            <p className="text-base-content/70 text-sm">
              Free tier: <strong>100 points/min</strong>. Pro tier: <strong>500 points/min</strong>.
            </p>
          </div>

          {/* Errors */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              <h3 className="text-lg font-semibold">Errors</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>400</td>
                    <td><code className="text-xs">VALIDATION_ERROR</code></td>
                    <td className="text-base-content/60">Invalid request body or parameters</td>
                  </tr>
                  <tr>
                    <td>401</td>
                    <td><code className="text-xs">UNAUTHORIZED</code></td>
                    <td className="text-base-content/60">Missing or invalid API key</td>
                  </tr>
                  <tr>
                    <td>403</td>
                    <td><code className="text-xs">TOKEN_LIMIT_EXCEEDED</code></td>
                    <td className="text-base-content/60">Out of HyPeMa tokens</td>
                  </tr>
                  <tr>
                    <td>404</td>
                    <td><code className="text-xs">NOT_FOUND</code></td>
                    <td className="text-base-content/60">Resource not found</td>
                  </tr>
                  <tr>
                    <td>429</td>
                    <td><code className="text-xs">RATE_LIMITED</code></td>
                    <td className="text-base-content/60">Rate limit exceeded</td>
                  </tr>
                  <tr>
                    <td>500</td>
                    <td><code className="text-xs">INTERNAL_ERROR</code></td>
                    <td className="text-base-content/60">Server error</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* AI Operations                                                       */}
        {/* ------------------------------------------------------------------ */}
        <section id="ai-operations" className="space-y-6">
          <h2 className="text-2xl font-bold">AI Operations</h2>

          {/* 1 - Research Company */}
          <EndpointCard
            id="research-company"
            method="POST"
            path="/ai/research-company"
            summary="Research a company by website URL"
            description="Analyzes a company website using AI-powered web search. Returns structured data about the company name, industry, products, leadership, and target audience. Supports SSE streaming for real-time status updates."
            timing="30s-5min"
            cost="5-15 tokens"
            sse
            requestBody={[
              {
                name: 'url',
                type: 'string',
                required: true,
                description: 'The company website URL to research.',
                example: '"https://example.com"',
              },
            ]}
            responseExample={`{
  "data": {
    "companyName": "ExampleCo",
    "description": "A leading SaaS company...",
    "industry": "Technology",
    "productName": "ExampleProduct",
    "valueProposition": "Helps teams collaborate...",
    "targetAudience": "Enterprise companies...",
    "ceoName": "Jane Smith",
    "ceoRole": "CEO & Co-founder"
  },
  "error": null,
  "meta": {
    "requestId": "req_abc123",
    "tokensConsumed": 8
  }
}`}
            curl={`curl -X POST https://api.hypema.app/api/v1/ai/research-company \\
  -H "Authorization: Bearer hpm_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com"}'`}
          />

          {/* 2 - Generate Leads */}
          <EndpointCard
            id="generate-leads"
            method="POST"
            path="/ai/generate-leads"
            summary="Find real leads using AI web search"
            description="Uses AI-powered web search to discover real, verified leads matching your criteria. Creates a new lead group and saves all found leads to your database. Supports SSE streaming."
            timing="30s-5min"
            cost="10-30 tokens"
            sse
            requestBody={[
              {
                name: 'industry',
                type: 'string',
                required: true,
                description: 'Target industry.',
                example: '"accounting"',
              },
              {
                name: 'roleTitle',
                type: 'string',
                required: false,
                description: 'Job role to target.',
                example: '"Managing Partner"',
              },
              {
                name: 'region',
                type: 'string',
                required: false,
                description: 'Geographic region.',
                example: '"Munich, Germany"',
              },
              {
                name: 'count',
                type: 'integer',
                required: false,
                description: 'Number of leads to find. Default 5, max 50.',
                example: '5',
              },
              {
                name: 'companySize',
                type: 'string',
                required: false,
                description: 'Company size filter.',
                example: '"10-50 employees"',
              },
              {
                name: 'excludeGroupIds',
                type: 'string[]',
                required: false,
                description: 'Lead group IDs to exclude duplicates from.',
              },
            ]}
            responseExample={`{
  "data": {
    "leads": [
      {
        "id": "...",
        "email": "jane@example.com",
        "firstName": "Jane",
        "lastName": "Smith",
        "company": "ExampleCo",
        "roleTitle": "CTO",
        "country": "Germany"
      }
    ],
    "count": 3,
    "leadGroupId": "uuid-here"
  },
  "error": null,
  "meta": {
    "requestId": "req_def456",
    "tokensConsumed": 15
  }
}`}
            curl={`curl -X POST https://api.hypema.app/api/v1/ai/generate-leads \\
  -H "Authorization: Bearer hpm_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"industry": "accounting", "region": "Munich, Germany", "count": 5}'`}
          />
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Email Operations                                                    */}
        {/* ------------------------------------------------------------------ */}
        <section id="email-operations" className="space-y-6">
          <h2 className="text-2xl font-bold">Email Operations</h2>

          {/* 3 - Generate Email */}
          <EndpointCard
            id="generate-email"
            method="POST"
            path="/emails/generate"
            summary="Generate a personalized email for a lead"
            description="Uses AI to generate a hyper-personalized cold email for a specific lead. The email is saved to the database and the lead status is updated to 'generated'. Requires topicGoalText describing what you're selling/pitching."
            timing="10-30s"
            cost="2-5 tokens"
            requestBody={[
              {
                name: 'leadId',
                type: 'string (uuid)',
                required: true,
                description: 'The lead to generate an email for.',
              },
              {
                name: 'topicGoalText',
                type: 'string',
                required: true,
                description: 'Describe your product/pitch/goal. Min 10 chars.',
                example: '"Introducing our AI accounting software that automates bookkeeping"',
              },
              {
                name: 'feedbackHistory',
                type: 'string[]',
                required: false,
                description: 'Previous feedback to refine the email style.',
              },
            ]}
            responseExample={`{
  "data": {
    "id": "email-uuid",
    "subject": "Quick question about your bookkeeping",
    "body": "Hi Jane,\\n\\nI noticed ExampleCo has been growing...",
    "unsubscribeUrl": "https://api.hypema.app/unsubscribe/token123"
  },
  "error": null,
  "meta": {
    "requestId": "req_ghi789",
    "tokensConsumed": 3
  }
}`}
            curl={`curl -X POST https://api.hypema.app/api/v1/emails/generate \\
  -H "Authorization: Bearer hpm_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"leadId": "lead-uuid-here", "topicGoalText": "Introducing our AI accounting software that automates bookkeeping"}'`}
          />

          {/* 4 - Generate Email Batch */}
          <EndpointCard
            id="generate-email-batch"
            method="POST"
            path="/emails/generate-batch"
            summary="Generate emails for multiple leads at once"
            description="Generates personalized emails for all leads in a lead group. For groups with 10+ leads, this is more efficient than calling generateEmail individually. Each email is saved to the database."
            timing="1-10min (depends on lead count)"
            cost="2-5 tokens per lead"
            requestBody={[
              {
                name: 'leadGroupId',
                type: 'string (uuid)',
                required: true,
                description: 'The lead group to generate emails for.',
              },
              {
                name: 'topicGoalText',
                type: 'string',
                required: false,
                description: 'Your pitch/goal text. Min 10 chars.',
              },
              {
                name: 'feedbackHistory',
                type: 'string[]',
                required: false,
                description: 'Style feedback.',
              },
              {
                name: 'leadIds',
                type: 'string[]',
                required: false,
                description: 'Specific lead IDs to generate for (subset of group).',
              },
            ]}
            responseExample={`{
  "data": {
    "results": [
      {
        "id": "email-uuid",
        "subject": "...",
        "body": "...",
        "unsubscribeUrl": "..."
      }
    ],
    "failedLeads": []
  },
  "error": null,
  "meta": {
    "requestId": "req_jkl012",
    "tokensConsumed": 25
  }
}`}
            curl={`curl -X POST https://api.hypema.app/api/v1/emails/generate-batch \\
  -H "Authorization: Bearer hpm_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"leadGroupId": "group-uuid-here", "topicGoalText": "Introducing our AI accounting software"}'`}
          />

          {/* 5 - Get Email */}
          <EndpointCard
            id="get-email"
            method="GET"
            path="/emails/:id"
            summary="Retrieve a generated email by ID"
            description="Fetches a previously generated email. Use this to review an email before building the draft payload for sending."
            timing="< 1s"
            cost="Free"
            pathParams={[
              {
                name: 'id',
                type: 'string (uuid)',
                required: true,
                description: 'The generated email ID.',
              },
            ]}
            responseExample={`{
  "data": {
    "id": "email-uuid",
    "leadId": "lead-uuid",
    "subject": "Quick question about your bookkeeping",
    "body": "Hi Jane,...",
    "unsubscribeUrl": "https://..."
  },
  "error": null,
  "meta": {
    "requestId": "req_mno345"
  }
}`}
            curl={`curl https://api.hypema.app/api/v1/emails/email-uuid-here \\
  -H "Authorization: Bearer hpm_live_your_key_here"`}
          />

          {/* 6 - Build Draft Payload */}
          <EndpointCard
            id="build-draft-payload"
            method="POST"
            path="/emails/drafts/payload"
            summary="Build a ready-to-send email payload"
            description="Takes a lead ID and generated email ID, validates the email hasn't expired and the recipient isn't suppressed, marks the lead as 'drafted', and returns a payload with provider, recipient address, subject, and body — everything you need to send via Gmail API, SMTP, or any email service."
            timing="< 1s"
            cost="Free"
            requestBody={[
              {
                name: 'leadId',
                type: 'string (uuid)',
                required: true,
                description: 'The lead this email is for.',
              },
              {
                name: 'generatedEmailId',
                type: 'string (uuid)',
                required: true,
                description: 'The generated email ID.',
              },
            ]}
            responseExample={`{
  "data": {
    "provider": "gmail",
    "to": "jane@example.com",
    "subject": "Quick question about your bookkeeping",
    "body": "Hi Jane,..."
  },
  "error": null,
  "meta": {
    "requestId": "req_pqr678"
  }
}`}
            curl={`curl -X POST https://api.hypema.app/api/v1/emails/drafts/payload \\
  -H "Authorization: Bearer hpm_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"leadId": "lead-uuid", "generatedEmailId": "email-uuid"}'`}
          />
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Leads                                                               */}
        {/* ------------------------------------------------------------------ */}
        <section id="leads" className="space-y-6">
          <h2 className="text-2xl font-bold">Leads</h2>

          {/* 7 - List Leads */}
          <EndpointCard
            id="list-leads"
            method="GET"
            path="/leads"
            summary="List leads with pagination and filters"
            timing="< 1s"
            cost="Free"
            queryParams={[
              {
                name: 'page',
                type: 'integer',
                required: false,
                description: 'Page number. Default 1.',
              },
              {
                name: 'per_page',
                type: 'integer',
                required: false,
                description: 'Results per page. Default 20, max 100.',
              },
              {
                name: 'status',
                type: 'string',
                required: false,
                description: 'Filter by status: new | generated | drafted | sent.',
              },
              {
                name: 'search',
                type: 'string',
                required: false,
                description: 'Search by name, email, or company.',
              },
            ]}
            responseExample={`{
  "data": [
    {
      "id": "...",
      "email": "jane@example.com",
      "firstName": "Jane",
      "lastName": "Smith",
      "company": "ExampleCo",
      "roleTitle": "CTO",
      "country": "Germany",
      "sourceUrl": null,
      "notes": null,
      "status": "new",
      "createdAt": "2026-04-01T12:00:00Z"
    }
  ],
  "error": null,
  "meta": {
    "requestId": "req_stu901",
    "tokensConsumed": 0,
    "pagination": {
      "total": 42,
      "page": 1,
      "perPage": 20,
      "hasMore": true
    }
  }
}`}
            curl={`curl "https://api.hypema.app/api/v1/leads?page=1&per_page=20&status=new" \\
  -H "Authorization: Bearer hpm_live_your_key_here"`}
          />

          {/* 8 - Create Lead */}
          <EndpointCard
            id="create-lead"
            method="POST"
            path="/leads"
            summary="Create a single lead"
            timing="< 1s"
            cost="Free"
            requestBody={[
              {
                name: 'email',
                type: 'string',
                required: true,
                description: "Lead's email address.",
              },
              {
                name: 'firstName',
                type: 'string',
                required: false,
                description: 'First name.',
              },
              {
                name: 'lastName',
                type: 'string',
                required: false,
                description: 'Last name.',
              },
              {
                name: 'company',
                type: 'string',
                required: false,
                description: 'Company name.',
              },
              {
                name: 'roleTitle',
                type: 'string',
                required: false,
                description: 'Job title.',
              },
              {
                name: 'country',
                type: 'string',
                required: false,
                description: 'Country or location.',
              },
              {
                name: 'sourceUrl',
                type: 'string',
                required: false,
                description: 'Where you found this lead.',
              },
              {
                name: 'notes',
                type: 'string',
                required: false,
                description: 'Additional notes.',
              },
              {
                name: 'leadGroupId',
                type: 'string (uuid)',
                required: false,
                description: 'Assign to a lead group.',
              },
            ]}
            responseExample={`{
  "data": {
    "id": "new-lead-uuid",
    "email": "jane@example.com",
    "firstName": "Jane",
    "lastName": "Smith",
    "company": "ExampleCo",
    "roleTitle": "CTO",
    "country": "Germany",
    "sourceUrl": null,
    "notes": null,
    "status": "new",
    "createdAt": "2026-04-01T12:00:00Z"
  },
  "error": null,
  "meta": {
    "requestId": "req_vwx234",
    "tokensConsumed": 0
  }
}`}
            curl={`curl -X POST https://api.hypema.app/api/v1/leads \\
  -H "Authorization: Bearer hpm_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "jane@example.com", "firstName": "Jane", "lastName": "Smith", "company": "ExampleCo"}'`}
          />

          {/* 9 - Get Lead */}
          <EndpointCard
            id="get-lead"
            method="GET"
            path="/leads/:id"
            summary="Get a single lead by ID"
            timing="< 1s"
            cost="Free"
            pathParams={[
              {
                name: 'id',
                type: 'string (uuid)',
                required: true,
                description: 'The lead ID.',
              },
            ]}
            responseExample={`{
  "data": {
    "id": "lead-uuid",
    "email": "jane@example.com",
    "firstName": "Jane",
    "lastName": "Smith",
    "company": "ExampleCo",
    "roleTitle": "CTO",
    "country": "Germany",
    "sourceUrl": null,
    "notes": null,
    "status": "new",
    "createdAt": "2026-04-01T12:00:00Z"
  },
  "error": null,
  "meta": {
    "requestId": "req_abc012",
    "tokensConsumed": 0
  }
}`}
            curl={`curl https://api.hypema.app/api/v1/leads/lead-uuid-here \\
  -H "Authorization: Bearer hpm_live_your_key_here"`}
          />

          {/* 10 - Update Lead */}
          <EndpointCard
            id="update-lead"
            method="PATCH"
            path="/leads/:id"
            summary="Update a lead's fields"
            timing="< 1s"
            cost="Free"
            pathParams={[
              {
                name: 'id',
                type: 'string (uuid)',
                required: true,
                description: 'The lead ID.',
              },
            ]}
            requestBody={[
              {
                name: 'email',
                type: 'string',
                required: false,
                description: 'New email.',
              },
              {
                name: 'firstName',
                type: 'string | null',
                required: false,
                description: 'First name (null to clear).',
              },
              {
                name: 'lastName',
                type: 'string | null',
                required: false,
                description: 'Last name.',
              },
              {
                name: 'company',
                type: 'string | null',
                required: false,
                description: 'Company.',
              },
              {
                name: 'roleTitle',
                type: 'string | null',
                required: false,
                description: 'Job title.',
              },
              {
                name: 'country',
                type: 'string | null',
                required: false,
                description: 'Country.',
              },
              {
                name: 'notes',
                type: 'string | null',
                required: false,
                description: 'Notes.',
              },
              {
                name: 'status',
                type: 'enum',
                required: false,
                description: 'Status: new | generated | drafted | sent.',
              },
            ]}
            responseExample={`{
  "data": {
    "id": "lead-uuid",
    "email": "jane@example.com",
    "firstName": "Jane",
    "lastName": "Smith",
    "company": "NewCompanyName",
    "roleTitle": "CTO",
    "country": "Germany",
    "sourceUrl": null,
    "notes": "Updated via API",
    "status": "new",
    "createdAt": "2026-04-01T12:00:00Z"
  },
  "error": null,
  "meta": {
    "requestId": "req_def345",
    "tokensConsumed": 0
  }
}`}
            curl={`curl -X PATCH https://api.hypema.app/api/v1/leads/lead-uuid-here \\
  -H "Authorization: Bearer hpm_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"company": "NewCompanyName", "notes": "Updated via API"}'`}
          />

          {/* 11 - Delete Lead */}
          <EndpointCard
            id="delete-lead"
            method="DELETE"
            path="/leads/:id"
            summary="Delete a lead permanently"
            description="Permanently removes a lead. This action is irreversible."
            timing="< 1s"
            cost="Free"
            pathParams={[
              {
                name: 'id',
                type: 'string (uuid)',
                required: true,
                description: 'The lead ID.',
              },
            ]}
            responseExample={`{
  "data": null,
  "error": null,
  "meta": {
    "requestId": "req_yza567"
  }
}`}
            curl={`curl -X DELETE https://api.hypema.app/api/v1/leads/lead-uuid-here \\
  -H "Authorization: Bearer hpm_live_your_key_here"`}
          />
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Lead Groups                                                         */}
        {/* ------------------------------------------------------------------ */}
        <section id="lead-groups" className="space-y-6">
          <h2 className="text-2xl font-bold">Lead Groups</h2>

          {/* 12 - List Lead Groups */}
          <EndpointCard
            id="list-lead-groups"
            method="GET"
            path="/lead-groups"
            summary="List all lead groups"
            timing="< 1s"
            cost="Free"
            responseExample={`{
  "data": [
    {
      "id": "group-uuid",
      "name": "Munich Accountants",
      "source": "ai",
      "createdAt": "2026-04-01T12:00:00Z",
      "leadCount": 15
    }
  ],
  "error": null,
  "meta": {
    "requestId": "req_bcd890"
  }
}`}
            curl={`curl https://api.hypema.app/api/v1/lead-groups \\
  -H "Authorization: Bearer hpm_live_your_key_here"`}
          />

          {/* 13 - Create Lead Group */}
          <EndpointCard
            id="create-lead-group"
            method="POST"
            path="/lead-groups"
            summary="Create a new lead group"
            timing="< 1s"
            cost="Free"
            requestBody={[
              {
                name: 'name',
                type: 'string',
                required: true,
                description: 'Group name. Max 200 characters.',
              },
              {
                name: 'source',
                type: 'string',
                required: false,
                description: 'How this group was created: manual | csv | ai. Default: manual.',
              },
            ]}
            responseExample={`{
  "data": {
    "id": "new-group-uuid",
    "name": "My API Group",
    "source": "manual",
    "createdAt": "2026-04-01T12:00:00Z",
    "leadCount": 0
  },
  "error": null,
  "meta": {
    "requestId": "req_efg123"
  }
}`}
            curl={`curl -X POST https://api.hypema.app/api/v1/lead-groups \\
  -H "Authorization: Bearer hpm_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "My API Group"}'`}
          />

          {/* 14 - Get Lead Group Leads */}
          <EndpointCard
            id="get-lead-group-leads"
            method="GET"
            path="/lead-groups/:id/leads"
            summary="Get all leads in a group"
            timing="< 1s"
            cost="Free"
            pathParams={[
              {
                name: 'id',
                type: 'string (uuid)',
                required: true,
                description: 'The lead group ID.',
              },
            ]}
            responseExample={`{
  "data": [
    {
      "id": "...",
      "email": "...",
      "firstName": "...",
      "status": "new"
    }
  ],
  "error": null,
  "meta": {
    "requestId": "req_hij456"
  }
}`}
            curl={`curl https://api.hypema.app/api/v1/lead-groups/group-uuid-here/leads \\
  -H "Authorization: Bearer hpm_live_your_key_here"`}
          />
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Usage                                                               */}
        {/* ------------------------------------------------------------------ */}
        <section id="usage" className="space-y-6">
          <h2 className="text-2xl font-bold">Usage</h2>

          {/* 15 - Get Usage */}
          <EndpointCard
            id="get-usage"
            method="GET"
            path="/usage"
            summary="Get current token balance"
            description="Returns your current HyPeMa token usage and limits. Use this to check remaining tokens before making AI calls."
            timing="< 1s"
            cost="Free"
            responseExample={`{
  "data": {
    "allowed": true,
    "used": 520,
    "limit": 5000,
    "remaining": 4480
  },
  "error": null,
  "meta": {
    "requestId": "req_klm789",
    "tokensConsumed": 0,
    "tokensRemaining": 4480
  }
}`}
            curl={`curl https://api.hypema.app/api/v1/usage \\
  -H "Authorization: Bearer hpm_live_your_key_here"`}
          />
        </section>
      </div>
    </div>
  );
}
